import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/core/auth/auth";
import { OnboardingGate } from "@/components/auth/OnboardingGate";
import { OnboardingRoute } from "@/components/auth/OnboardingRoute";
import { OnboardingErrorBoundary } from "@/components/auth/OnboardingErrorBoundary";
import { RouteErrorBoundary } from "@/components/auth/RouteErrorBoundary";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Waitlist from "./pages/Waitlist";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Hipaa from "./pages/legal/Hipaa";
import AuthCallback from "./pages/auth/Callback";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import SecuritySettings from "./pages/settings/Security";
import Onboarding from "./pages/app/Onboarding";
import Pricing from "./pages/Pricing";
import Billing from "./pages/app/billing";
import BillingPlans from "./pages/app/billing/Plans";
import BillingSuccess from "./pages/app/billing/Success";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminFeatureFlags from "./pages/admin/FeatureFlags";
import AdminAuditLog from "./pages/admin/AuditLog";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminTemplates from "./pages/admin/Templates";
import AdminReportRuns from "./pages/admin/ReportRuns";
import AdminErrors from "./pages/admin/Errors";
import AdminWaitlist from "./pages/admin/WaitlistAdmin";
import AdminUserActivity from "./pages/admin/analytics/UserActivity";
import { PortalShell } from "./pages/portal/PortalShell";
import { PortalHome } from "./pages/portal/PortalHome";
import { PortalDownloads } from "./pages/portal/PortalDownloads";
import { PortalTemplateBuilder } from "./pages/portal/PortalTemplateBuilder";
import { PortalReportPreferences } from "./pages/portal/PortalReportPreferences";
import { PortalAccount } from "./pages/portal/PortalAccount";

const queryClient = new QueryClient();

/**
 * ProtectedRoute wrapper
 *
 * Wraps routes that require authentication.
 * Uses OnboardingGate to handle:
 * - BOOTING → Loading spinner with timeout
 * - UNAUTHENTICATED → Redirect to login
 * - GATED → Appropriate gate screen
 * - ERROR → Recovery UI
 * - AUTHENTICATED → Render children
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return <OnboardingGate>{children}</OnboardingGate>;
}


const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/hipaa" element={<Hipaa />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Auth flow routes */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* Settings routes (require auth) */}
            <Route path="/settings/security" element={
              <ProtectedRoute>
                <SecuritySettings />
              </ProtectedRoute>
            } />

            {/* Onboarding route - accessible to GATED users with ONBOARDING_REQUIRED */}
            <Route path="/app/onboarding" element={
              <OnboardingErrorBoundary>
                <OnboardingRoute>
                  <Onboarding />
                </OnboardingRoute>
              </OnboardingErrorBoundary>
            } />

            {/* Protected platform routes - require AUTHENTICATED state */}
            <Route path="/app" element={<Navigate to="/portal" replace />} />
            <Route path="/portal" element={
              <ProtectedRoute>
                <PortalShell />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<PortalHome />} />
              <Route path="downloads" element={<PortalDownloads />} />
              <Route path="templates" element={<PortalTemplateBuilder />} />
              <Route path="report-preferences" element={<PortalReportPreferences />} />
              <Route path="account" element={<PortalAccount />} />
              <Route path="billing" element={<Navigate to="/app/billing" replace />} />
            </Route>

            {/* Billing routes */}
            <Route path="/app/billing" element={
              <RouteErrorBoundary routeName="Billing" fallbackPath="/portal" fallbackLabel="Return to Portal">
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/app/billing/plans" element={
              <RouteErrorBoundary routeName="Billing Plans" fallbackPath="/app/billing" fallbackLabel="Return to Billing">
                <ProtectedRoute>
                  <BillingPlans />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/app/billing/success" element={
              <RouteErrorBoundary routeName="Billing Success" fallbackPath="/portal" fallbackLabel="Return to Portal">
                <ProtectedRoute>
                  <BillingSuccess />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />

            {/* Admin routes - AdminLayout handles admin-specific authorization */}
            <Route path="/admin" element={
              <RouteErrorBoundary routeName="Admin" fallbackPath="/portal" fallbackLabel="Return to Portal">
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/users" element={
              <RouteErrorBoundary routeName="Admin Users" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/feature-flags" element={
              <RouteErrorBoundary routeName="Feature Flags" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminFeatureFlags />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/audit-log" element={
              <RouteErrorBoundary routeName="Audit Log" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminAuditLog />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/analytics" element={
              <RouteErrorBoundary routeName="Analytics" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/analytics/users" element={
              <RouteErrorBoundary routeName="User Activity" fallbackPath="/admin/analytics" fallbackLabel="Return to Analytics">
                <ProtectedRoute>
                  <AdminUserActivity />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/templates" element={
              <RouteErrorBoundary routeName="Templates" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminTemplates />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/report-runs" element={
              <RouteErrorBoundary routeName="Report Runs" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminReportRuns />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/errors" element={
              <RouteErrorBoundary routeName="Errors" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminErrors />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />
            <Route path="/admin/waitlist" element={
              <RouteErrorBoundary routeName="Waitlist" fallbackPath="/admin" fallbackLabel="Return to Admin">
                <ProtectedRoute>
                  <AdminWaitlist />
                </ProtectedRoute>
              </RouteErrorBoundary>
            } />

            {/* Legacy redirects - send to portal */}
            <Route path="/dashboard" element={<Navigate to="/portal" replace />} />
            <Route path="/dictation" element={<Navigate to="/portal" replace />} />
            <Route path="/app/livespeak" element={<Navigate to="/portal" replace />} />
            <Route path="/app/livespeak/*" element={<Navigate to="/portal" replace />} />
            <Route path="/app/downloads" element={<Navigate to="/portal/downloads" replace />} />
            <Route path="/app/macros" element={<Navigate to="/portal/report-preferences" replace />} />
            <Route path="/demo" element={<Navigate to="/portal" replace />} />
            <Route path="/liveinsight" element={<Navigate to="/portal" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
